import { API_URL } from 'config';
import { makeAPICall } from 'models/api';
import { BFMCMin } from './bfmc';

export declare namespace API {
    export interface User {
        id: number;
        username: string;
        first_name: string;
        last_name: string;
        email: string;
        roles: string[];
        bmcs: BFMCMin[];
        is_active: boolean;
        cognito_status?: string;
    }

    export interface UserList {
        users: User[];
        total: number;
    }

    export interface ActionResponse {
        success: boolean;
        error?: string;
    }
}

export interface User {
    id: number;
    fullName: string;
    userName: string;
    firstName: string;
    lastName: string;
    email: string;
    roles: string[];
    BFMCs: BFMCMin[];
    isActive: boolean;
    cognitoStatus?: string;
}

export interface UserList {
    users: User[];
    total: number;
}

export interface ActionResponse {
    success: boolean;
    error?: string;
}

export type NewUser = Omit<
    User,
    'id' | 'fullName' | 'userName' | 'isActive' | 'cognitoStatus' | 'BFMCs'
> & { BFMCs: string[] };

export type UpdateUser = Omit<
    User,
    'fullName' | 'userName' | 'email' | 'cognitoStatus' | 'BFMCs' | 'isActive'
> & {
    BFMCs: string[];
    cognitoAction: string;
};

export type UpdateSelf = Omit<
    User,
    | 'id'
    | 'fullName'
    | 'userName'
    | 'email'
    | 'cognitoStatus'
    | 'BFMCs'
    | 'isActive'
    | 'roles'
>;

const USER_ROUTE = `${API_URL}/users/`;

export const mapAPIToUser = ({
    username,
    first_name,
    last_name,
    is_active,
    bmcs,
    ...rest
}: API.User): User => ({
    ...rest,
    userName: username,
    firstName: first_name,
    lastName: last_name,
    fullName: `${first_name} ${last_name}`,
    isActive: is_active,
    BFMCs: bmcs,
});

export const getUsers = makeAPICall<
    UserList,
    { search_for: string; skip: number; limit: number },
    API.UserList
>(
    (payload) => ({
        url: `${USER_ROUTE}?search_for=${payload.search_for}&skip=${payload.skip}&limit=${payload.limit}`,
    }),
    ({ users, total }) => ({
        users: users.map((x) => mapAPIToUser(x)),
        total: total,
    }),
);

export const getUser = makeAPICall<User, { user_id: number }, API.User>(
    (payload) => ({
        url: `${USER_ROUTE}${payload.user_id}`,
    }),
    ({
        id,
        email,
        first_name,
        last_name,
        roles,
        is_active,
        username,
        bmcs,
        cognito_status,
    }) => ({
        id,
        userName: username,
        fullName: `${first_name} ${last_name}`,
        firstName: first_name,
        lastName: last_name,
        email,
        roles,
        isActive: is_active,
        cognitoStatus: cognito_status,
        BFMCs: bmcs,
    }),
);

export const addNewUser = makeAPICall<null, { user: NewUser }>(
    (payload) => {
        const { user } = payload;
        const data = new FormData();
        data.append('email', user.email);
        data.append('first_name', user.firstName);
        data.append('last_name', user.lastName);
        user.roles.forEach((role) => {
            data.append('roles', role);
        });
        user.BFMCs.forEach((bfmc) => {
            data.append('bmcs', bfmc);
        });

        return {
            url: `${USER_ROUTE}`,
            contentType: null,
            requestData: {
                method: 'POST',
                body: data,
            },
        };
    },
    () => null,
);

export const deleteUser = makeAPICall<
    ActionResponse,
    { user_id: string },
    API.ActionResponse
>(
    (payload) => {
        return {
            url: `${USER_ROUTE}${payload.user_id}`,
            contentType: null,
            requestData: {
                method: 'DELETE',
            },
        };
    },
    (x) => x,
);

export const updateUser = makeAPICall<
    ActionResponse,
    { user: UpdateUser },
    API.ActionResponse
>(
    (payload) => {
        const { user } = payload;
        const data = new FormData();
        data.append('first_name', user.firstName);
        data.append('last_name', user.lastName);
        user.roles.forEach((role) => {
            data.append('roles', role);
        });
        user.BFMCs.forEach((bfmc) => {
            data.append('bmcs', bfmc);
        });

        data.append('cognito_action', user.cognitoAction);
        return {
            url: `${USER_ROUTE}${user.id}`,
            contentType: null,
            requestData: {
                method: 'PUT',
                body: data,
            },
        };
    },
    (x) => x,
);
